import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import { emptyObject } from 'ui-components/default-props/empty';

import { useFsuiContext } from '../financial-services-ui/fsui-context-provider';
import { getBillingInfoFromPaymentMethod } from '../financial-services-ui/mapping';
import { usePaymentAndSubscriptionMegaContext } from '../pages/settings/payment-and-subscription/context';

/**
 * @typedef {import('@shipstation/financial-services-ui-components').StoredPaymentMethod} StoredPaymentMethod
 * @typedef {import('@shipstation/financial-services-ui-components').TransactionCategory} TransactionCategory
 * @typedef {import('@shipstation/financial-services-ui-components').BillingInfoData} BillingInfoData
 */

/**
 * Returns the first payment method in the list that has assigned preferences, or the first payment method at all
 * @param {StoredPaymentMethod[]} methods
 * @returns {StoredPaymentMethod | null}
 */
const getFallbackPaymentMethod = (/** @type {StoredPaymentMethod[]} */ methods = []) => {
  return methods.find((method) => method.assignedPreferences?.length > 0) ?? methods[0] ?? null;
};

/**
 * @param {object} params
 * @param {TransactionCategory} params.transactionCategory
 * @param {(billingInfo: BillingInfoData) => void} [params.onBillingInfoUpdated]
 * @param {StoredPaymentMethod} [params.initialPaymentMethod]
 * @param {boolean} [params.shouldUseFallbackPaymentMethod]
 * @returns {{
 *  handlePaymentMethodChange: (method: StoredPaymentMethod | null) => void,
 *  findPaymentMethodByTransactionCategory: (methods: StoredPaymentMethod[]) => StoredPaymentMethod | null
 *  preferredPaymentMethod: StoredPaymentMethod | null
 *  selectedPaymentMethod: StoredPaymentMethod | null
 *  paymentMethods: StoredPaymentMethod[] | null
 *  isBillingInfoLoading: boolean
 * }}
 */
export const usePaymentMethodManager = ({
  transactionCategory,
  onBillingInfoUpdated,
  initialPaymentMethod,
  shouldUseFallbackPaymentMethod = false,
}) => {
  const seller = useSelector((state) => state.seller) || emptyObject;

  const { paymentMethodDetails: paymentMethods, isBillingInfoLoading } = usePaymentAndSubscriptionMegaContext();

  const { setPaymentMethodId } = useFsuiContext();

  const findPaymentMethodByTransactionCategory = useCallback(
    (/** @type {StoredPaymentMethod[]} */ methods = []) => {
      return methods.find((method) => method.assignedPreferences?.includes(transactionCategory)) || null;
    },
    [transactionCategory],
  );

  const preferredPaymentMethod = useMemo(
    () =>
      initialPaymentMethod ??
      findPaymentMethodByTransactionCategory(paymentMethods) ??
      (shouldUseFallbackPaymentMethod ? getFallbackPaymentMethod(paymentMethods) : null),
    [findPaymentMethodByTransactionCategory, initialPaymentMethod, paymentMethods, shouldUseFallbackPaymentMethod],
  );

  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(preferredPaymentMethod);

  useEffect(() => {
    if (preferredPaymentMethod) {
      setPaymentMethodId?.(preferredPaymentMethod.id);
    }
  }, [preferredPaymentMethod, setPaymentMethodId]);

  const updateBillingInfo = useCallback(
    (/** @type {StoredPaymentMethod | null} */ method) => {
      const billingInfo = getBillingInfoFromPaymentMethod(method, seller || emptyObject);
      onBillingInfoUpdated?.(billingInfo);
    },
    [onBillingInfoUpdated, seller],
  );

  const handlePaymentMethodChange = useCallback(
    (/** @type {StoredPaymentMethod} */ method) => {
      setPaymentMethodId?.(method?.id || null);
      setSelectedPaymentMethod(method);
      updateBillingInfo(method);
    },
    [setPaymentMethodId, updateBillingInfo],
  );

  return {
    handlePaymentMethodChange,
    findPaymentMethodByTransactionCategory,
    preferredPaymentMethod,
    selectedPaymentMethod,
    paymentMethods: paymentMethods || null,
    isBillingInfoLoading,
  };
};
